import { createContext, useContext, useMemo, type ReactNode } from "react";
import { fill } from "./fill";
import { kitLabels, type KitLabels } from "./kit-labels";
import { DEFAULT_LOCALE } from "./time";

export interface KitCopy {
  /** BCP 47 tag for `Intl` (dates, spoken durations), e.g. "pl-PL". Default en-GB. */
  locale: string;
  labels: KitLabels;
}
const Ctx = createContext<KitCopy>({ locale: DEFAULT_LOCALE, labels: kitLabels });

/**
 * The page's language for the kit (REQUESTS #141 a, D22b): `locale` for dates and durations, `labels` for every word
 * the kit shows or announces on its own (see `KitLabels`). Missing labels fall back to English. Nestable: an inner
 * provider overrides some labels for its subtree. Without a provider the kit renders English and en-GB dates, as before.
 */
export function KitCopyProvider({ locale, labels, children }: { locale?: string; labels?: Partial<KitLabels>; children: ReactNode }) {
  const parent = useContext(Ctx);
  const value = useMemo<KitCopy>(() => ({ locale: locale ?? parent.locale, labels: { ...parent.labels, ...labels } }), [locale, labels, parent]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** The provider's copy with a component's own overrides on top (its `locale` and `labels` props). */
export function useKitCopy(locale?: string, labels?: Partial<KitLabels>): KitCopy {
  const c = useContext(Ctx);
  return locale === undefined && labels === undefined ? c : { locale: locale ?? c.locale, labels: labels ? { ...c.labels, ...labels } : c.labels };
}

/**
 * Renders a message with `<b>…</b>` parts in bold (`dueLocalUtc`, `typeToConfirm`, `passwordStrength`); other text is
 * plain. `values` fill `{name}` placeholders after the tags are split, so a value containing `<b>` stays text.
 */
export function withBold(message: string, boldClass?: string, values?: Record<string, string | number>): ReactNode[] {
  return message.split(/<b>(.*?)<\/b>/).map((raw, i) => {
    const part = values ? fill(raw, values) : raw;
    return i % 2 ? (
      <span key={i} className={boldClass}>
        {part}
      </span>
    ) : (
      part
    );
  });
}
